import { useField, useFormikContext } from 'formik';
import React from 'react';
import { ConfigProvider } from 'antd';

import { FormFieldError, FormFieldLabel } from './components';

import './index.scss';
import classNames from 'classnames';
import { formFieldId } from 'src/utils';

interface IProps {
  children: React.ReactElement;
  label?: string;
  name: string;
  disabled?: boolean;
  floating?: boolean;
  wrapperClassNames?: string;
  labelClassNames?: string;
  fieldClassNames?: string;
  error?: string | null;
  size?: 'small' | 'medium';
}

/**
 * Wrapper for form field that includes some design and children component
 * @param children input element
 * @param name input element name objects deep marking as dot user.name etc
 * @param label input element label-title
 * @param disabled input element disabled status manually
 * @param floating for radio, checkbox etc
 * @param wrapperClassNames wrapper classnames
 * @param labelClassNames custom input label classnames
 * @param fieldClassNames custom classnames for fields
 * @param error custom error
 */

export const FormField = ({ children, label, name, disabled = false, floating = true, wrapperClassNames, labelClassNames, fieldClassNames, error, size = 'medium' }: IProps) => {
  const [focused, setFocused] = React.useState(false);
  const [_field, meta, _helpers] = useField(name);
  const { isSubmitting } = useFormikContext();

  const className = classNames('field_wrapper', wrapperClassNames, {
    floating: floating,
    disabled: disabled,
  });

  const fieldId = formFieldId(name, 'FormField');
  const errorId = formFieldId(name, 'Error');
  const labelId = formFieldId(name, 'Label');

  const hasError = error;
  const hasMetaError = meta.error && Boolean(meta.touched);

  const isDisabled = disabled || isSubmitting;

  return (
    <ConfigProvider
      theme={{
        token: {
          controlHeight: size === 'small' ? 32 : 54,
          controlPaddingHorizontal: 24,
          colorTextDisabled: '#343434',
        },
        components: {
          Radio: {
            borderRadius: 24,
            buttonSolidCheckedColor: '#0958d9',
          },
        },
      }}
    >
      <>
        <div
          className={className}
          id={formFieldId(name, 'Wrapper')}
        >
          <div
            className={classNames('field_inner', {
              'field_inner--error': error || (meta.error && Boolean(meta.touched)),
            })}
          >
            <FormFieldLabel
              id={labelId}
              htmlFor={fieldId}
              name={name}
              label={label}
              focused={focused}
              labelClassNames={labelClassNames}
            />
            <div
              className="field_component"
              id={formFieldId(name, '')}
            >
              {React.cloneElement(children, {
                name,
                label,
                disabled: isDisabled,
                variant: floating ? 'borderless' : undefined,
                onFocus: () => setFocused(true),
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  setFocused(false);
                  children.props.onBlur?.(e);
                },
                className: fieldClassNames,
                autoComplete: 'off',
                id: fieldId,
                'aria-disabled': isDisabled,
                'aria-describedby': hasError || hasMetaError ? errorId : undefined,
                'aria-invalid': hasError || hasMetaError ? 'true' : 'false',
                'aria-errormessage': hasError || hasMetaError ? errorId : undefined,
              })}
            </div>
          </div>
          {hasError ? (
            <FormFieldError
              id={errorId}
              error={error}
            />
          ) : (
            hasMetaError && (
              <FormFieldError
                id={errorId}
                error={hasMetaError ? meta.error : undefined}
              />
            )
          )}
        </div>
      </>
    </ConfigProvider>
  );
};
